import { escapeRegExp, isNil } from 'lodash';

import { logError } from '../../../utils/log';
import { API, get as getApi } from 'utils/api';
import { getItem, setItem } from 'utils/storage/sessionStorage';
import { getTransformedCountries } from 'utils/countries';
import { DEFAULT_COUNTRY_ISO } from 'utils/helper/common';

import type { CountryEntry, CountryModel, LocationGeo, ThunkAction } from 'types';

const PROVIDER_HEADERS = {
  Accept: 'application/vnd.teleport.v1+json'
};
const COUNTRIES_STORAGE_NAME = 'countries';
const COUNTRIES_COUNTRY_CODES_STORAGE_NAME = 'countries_country_code';
const { COUNTRIES } = API.END_POINTS.API;

const fetchCountries = async () => {
  try {
    const url = COUNTRIES;
    const savedCountries = getItem(COUNTRIES_STORAGE_NAME);

    if (savedCountries) {
      return JSON.parse(savedCountries);
    }

    const response = (await getApi(url)) as { data: Array<CountryModel> };
    const countries = getTransformedCountries(response.data);

    setItem(COUNTRIES_STORAGE_NAME, JSON.stringify(countries));

    return countries;
  } catch (error) {
    logError(error);

    return [];
  }
};

export const fetchCountryCodes = async (selectedCountry?: string) => {
  try {
    const url = COUNTRIES;
    const savedCountries = getItem(COUNTRIES_COUNTRY_CODES_STORAGE_NAME);

    let countries = savedCountries ? JSON.parse(savedCountries) : null;

    if (countries) {
      return countries;
    }
    const response = (await getApi(url)) as { data: Array<CountryModel> };

    countries = response.data.map((country) => ({
      label: `${country.niceName} (+${country.phoneCode})`,
      identifier: country.iso,
      value: `+${country.phoneCode}`,
      isSelected: (selectedCountry || DEFAULT_COUNTRY_ISO) === country.iso,
      isDisabled: false
    }));

    setItem(COUNTRIES_COUNTRY_CODES_STORAGE_NAME, JSON.stringify(countries));

    return countries;
  } catch (error) {
    logError(error);

    return [];
  }
};

const searchCityInternal = async (query: string): Promise<Array<LocationGeo>> => {
  try {
    const url = API.END_POINTS.LOCATIONS.getLocations();
    const { data } = await getApi(url, {
      params: {
        search: query
      },
      headers: PROVIDER_HEADERS
    });

    return data.map(({ id, fullName, name, state, country, countryCode }) => ({
      id,
      fullName,
      city: name,
      state,
      country,
      countryCode
    }));
  } catch (error) {
    logError(error);

    throw error;
  }
};

export function searchCity(query: string): ThunkAction {
  return () => {
    try {
      if (isNil(query) || query.trim().length === 0) {
        return Promise.resolve([]);
      }

      return searchCityInternal(query)
        .catch(() => []);
    } catch (error) {
      logError(error);

      return Promise.resolve([]);
    }
  };
}

export function searchCountry(query: string): () => Promise<Array<CountryEntry>> {
  return () => {
    const r = new RegExp(escapeRegExp(query), 'i');

    return fetchCountries()
      .then(countries => Promise.resolve(countries.filter((country) => r.test(country.country.toLowerCase()))));
  };
}
